import type { WrapStyle } from '@/lib/object-effects/types/wrap-style'
import { DEFAULT_OFFSET } from '../constants/wrap-offsets'
import type {
  CommitEntry,
  OffsetKey,
  StyleOffsetMemory,
  WrapCarrier,
  WrapTarget,
} from '../types/wrap-offset'
import {
  type CommonOffset,
  aggregateCommonOffset,
  commonOffsetFromObj,
  offsetValue,
} from './offset-display'
import {
  fieldsForStyle,
  normWrapStyle,
  relevantOffsetKeysForStyle,
  sideEnabledForStyle,
  sideForKey,
} from './wrap-style-sides'

/** One target's style switch `from` -> `next` (see `applyWrapStyle`): `snapshot` is the leaving
 *  style's current values, to store back as `memory[from]`; `entry` writes `next` plus the entering
 *  style's remembered (or 6pt default) value for each of its relevant fields that differs. */
export function buildWrapStyleSwitch(
  target: WrapTarget,
  from: WrapStyle,
  next: WrapStyle,
  memory: StyleOffsetMemory
): { snapshot: Partial<Record<OffsetKey, number>>; entry: CommitEntry } {
  const snapshot: Partial<Record<OffsetKey, number>> = { ...memory[from] }

  for (const key of relevantOffsetKeysForStyle(from)) {
    const { field } = sideForKey(key)
    const raw = target.obj[field]

    if (raw !== undefined) snapshot[key] = raw
  }

  const beforeUpdate: Partial<WrapCarrier> = { wrapStyle: from }
  const afterUpdate: Partial<WrapCarrier> = { wrapStyle: next }
  const remembered = memory[next]

  for (const key of relevantOffsetKeysForStyle(next)) {
    const { field } = sideForKey(key)
    const restoredValue = remembered?.[key] ?? DEFAULT_OFFSET
    const rawBefore = target.obj[field]

    if (rawBefore !== restoredValue) {
      beforeUpdate[field] = rawBefore
      afterUpdate[field] = restoredValue
    }
  }

  return { snapshot, entry: { target, before: beforeUpdate, after: afterUpdate } }
}

/** One side's committed value `next`, for every target whose own style uses that side (a target
 *  whose style doesn't use it is skipped entirely, not patched with an inapplicable value). */
export function buildSideOffsetEntries(
  targets: WrapTarget[],
  key: OffsetKey,
  next: number
): CommitEntry[] {
  const { field } = sideForKey(key)
  const entries: CommitEntry[] = []

  for (const t of targets) {
    const style = normWrapStyle(t.obj)

    if (!sideEnabledForStyle(key, style)) continue

    const before = offsetValue(t.obj, field)

    if (next === before) continue
    entries.push({ target: t, before: { [field]: before }, after: { [field]: next } })
  }

  return entries
}

/** What the shared control should show once side `key` is set to `next` on every target whose
 *  style uses it — "Mixed" when the selection now disagrees. */
export function commonOffsetAfterSideEdit(
  targets: WrapTarget[],
  key: OffsetKey,
  next: number
): CommonOffset {
  const { field } = sideForKey(key)
  const perTarget = targets.map((t) => {
    const style = normWrapStyle(t.obj)
    const merged: WrapCarrier = sideEnabledForStyle(key, style)
      ? { ...t.obj, [field]: next }
      : t.obj

    return commonOffsetFromObj(merged, style)
  })

  return aggregateCommonOffset(perTarget)
}

/** The shared control's value `next` for every target, each writing only the fields real for its
 *  own current style (`fieldsForStyle`). */
export function buildAllOffsetsEntries(targets: WrapTarget[], next: number): CommitEntry[] {
  const entries: CommitEntry[] = []

  for (const t of targets) {
    const fieldsToPersist = fieldsForStyle(normWrapStyle(t.obj))
    const before: Partial<WrapCarrier> = {}
    const after: Partial<WrapCarrier> = {}

    for (const { field } of fieldsToPersist) {
      // Compare against the RAW stored value (may be `undefined`), not `offsetValue`'s display
      // fallback — an unset field only *looks* like it already matches `next` when `next`
      // happens to equal the 6pt default, but nothing has actually been persisted yet (see
      // `applyWrapStyle`).
      const rawBefore = t.obj[field]

      if (rawBefore === next) continue
      before[field] = rawBefore
      after[field] = next
    }

    if (Object.keys(after).length > 0) entries.push({ target: t, before, after })
  }

  return entries
}
